/**
 * Decision outcome vocabulary — THE source of truth for the values that land on a
 * `decisions` row's outcome axes.
 *
 * Why this module exists (bug sweep #4, 2026-08-04): the writers and the readers of these
 * attributes had silently drifted apart. Both writers — the synchronous
 * `@marva/shared/events.updateDecisionOutcome` and the Stream-driven
 * `outcome-denormalizer` — stamp `outcome = <eventType>` (`conversion`, `email_opened`, …)
 * and route rejections to a SEPARATE `negativeOutcome` attribute. But two governance
 * readers filtered on a vocabulary nobody has ever written:
 *
 *   - `hasCustomerConverted` looked for `outcome === 'converted'`, so the
 *     cancel-a-scheduled-send-when-the-customer-buys backstop never fired — cart recovery
 *     emails went out to people who had already checked out.
 *   - `checkFatigue` looked for `outcome === 'ignored' | 'dismissed'`, so per-channel
 *     fatigue counted zero forever and the cooldown extension never applied.
 *
 * Both looked correct in isolation. Neither did anything. Anything that needs to reason
 * about "did this decision convert / land / get rejected?" must import from here rather
 * than inlining string literals, so a future rename breaks loudly instead of going dark.
 *
 * MAR-87 routes synchronous and stream writes through one durable shared reducer.
 * These compatibility vocabularies describe historical response/purchase for
 * governance, never monetary success: a refunded purchase still happened.
 *
 * @module outcome-vocabulary
 */

'use strict';

// Dependency-free vocabularies only: neither import constructs clients or reads environment.
const { isHelpDecision, isReplyTurnDecision } = require('./decision-vocabulary');
const { DELIVERY_PROOF } = require('./outbound-settlement-vocabulary');

/**
 * Every value the positive `outcome` attribute can hold — i.e. the keys of OUTCOME_PRIORITY
 * in `events.js` and its mirror in outcome-denormalizer. Ordered lowest → highest priority.
 */
const POSITIVE_OUTCOMES = Object.freeze([
  // Behavioral (priority 0) — activity, not engagement with a Marva message
  'page_view',
  'product_view',
  'product_dwell',
  'cart_update',
  // Sent (1)
  'email_sent',
  'sms_sent',
  'chat_message_sent',
  'popup_displayed',
  // Delivered (2)
  'email_delivered',
  'sms_delivered',
  // Opened (3)
  'email_opened',
  // Clicked (4)
  'email_clicked',
  'sms_clicked',
  'chat_link_clicked',
  'chat_cta_clicked',
  'popup_cta_clicked',
  // Converted (5-6)
  'conversion_partial',
  'conversion',
  // Corrected legacy scalar, never a positive finality or financial admission gate.
  'refunded'
]);

/**
 * The customer bought. Cancels a still-scheduled outbound decision — sending "you left
 * something behind" to someone who just checked out is the classic embarrassing send.
 *
 * `conversion_partial` is defined in EVENT_TYPES but no producer emits it today; it is
 * included so the backstop works the moment one does.
 */
const CONVERSION_OUTCOMES = Object.freeze(['conversion', 'conversion_partial', 'refunded']);

/**
 * The customer actually responded — opened, clicked, or converted. Anything at or below
 * `delivered` means the message went out and nothing came back, which is what "ignored"
 * means for fatigue purposes.
 */
const ENGAGED_OUTCOMES = Object.freeze([
  'email_opened',
  'email_clicked',
  'sms_clicked',
  'chat_link_clicked',
  'chat_cta_clicked',
  'popup_cta_clicked',
  'conversion_partial',
  'conversion',
  'refunded'
]);

/**
 * Rejections that count as fatigue on a channel: the shopper actively waved the message
 * off. Deliberately EXCLUDES `opt_out` / `unsubscribed` / `complaint` / `email_complained`
 * — those are more severe, and channel suppression already hard-blocks that customer, so
 * counting them toward a soft cooldown extension would be redundant.
 *
 * These land on the `negativeOutcome` attribute, never on `outcome`.
 */
const DISMISSAL_NEGATIVE_OUTCOMES = Object.freeze([
  'nudge_dismissed',
  'popup_dismissed', // legacy alias — soft dismiss
  'negative_reaction'
]);

/** Delivery grades that positively establish customer reach. */
const DELIVERY_REACHED = Object.freeze([
  DELIVERY_PROOF.ACCEPTED,
  DELIVERY_PROOF.RENDERED,
  DELIVERY_PROOF.CONFIRMED
]);

/** Backwards-compatible export for readers that name the strongest negative evidence. */
const DELIVERY_NOT_REACHED = DELIVERY_PROOF.REFUTED;

/**
 * Grace period before an un-opened send counts as ignored. Without it, an email sent an
 * hour ago would be scored as ignored before the customer plausibly had a chance to open
 * it, and a burst of sends would fatigue a channel instantly.
 */
const IGNORED_AFTER_MS = 72 * 60 * 60 * 1000;

/**
 * Terminal statuses that mean the message never reached the customer. A decision in one of
 * these states is not a contact: it cannot have been ignored, it cannot fatigue a channel, and
 * it must not count toward a frequency cap or start a cooldown.
 *
 * This matters because the enforcer's own blocks land here. A cooldown withhold writes a
 * `suppressed` row; if that row then counts as a contact, the block extends the very cooldown
 * that produced it, counts against the weekly cap, and after IGNORED_AFTER_MS doubles the
 * required gap via fatigue's reduceFactor. Governance blocks were compounding on themselves.
 * ('failed' had the same effect before governance withholds were split out of it.)
 *
 * `dispatched` means only that work was handed to the send site. `pending_delivery` means a
 * socket frame is waiting for a future connection. Neither is delivery evidence. Keeping
 * either out of this list made a crash, refusal or absent socket spend a shopper's cap and
 * cooldown even though nobody was reached (MAR-60).
 */
const NOT_SENT_STATUSES = Object.freeze([
  'suppressed', 'cancelled', 'expired', 'failed', 'shadowed', 'escalated', 'scheduled',
  'dispatched', 'pending_delivery',
  // `decided` — the terminal status of a D2 `wait` judgment (item 4). Marva chose not to
  // act, so nothing was handed to a channel and nobody was reached.
  //
  // Wait judgments reach no channel and must not rely on every caller pairing this predicate
  // with a separate action check.
  'decided'
]);

/**
 * Did this decision actually reach the customer?
 *
 * Explicit delivery evidence wins over lifecycle status. `accepted`, `rendered` and
 * `confirmed` establish reach; `none` and `refuted` do not. This lets an accepted frame count
 * while its row is still in flight, and prevents dispatch alone from masquerading as delivery.
 *
 * Rows predating the proof axis keep the conservative status fallback: only a known not-sent
 * status returns false. A missing or unrecognised proof therefore does not silently erase
 * historical contacts. Caps, cooldown and fatigue ask `countsAsContact` below, which also
 * leaves out the shopper's own answered chat turns (MAR-242).
 */
function reachedCustomer(decision) {
  if (decision && Object.prototype.hasOwnProperty.call(decision, 'deliveryProof')) {
    if (DELIVERY_REACHED.includes(decision.deliveryProof)) return true;
    if ([DELIVERY_PROOF.NONE, DELIVERY_PROOF.REFUTED].includes(decision.deliveryProof)) return false;
  }
  if (!decision || !decision.status) return true;
  return !NOT_SENT_STATUSES.includes(decision.status);
}

/**
 * Does this decision count as a CONTACT — toward a frequency cap, the `minGapHours` cooldown
 * and channel fatigue? The one question every caps/cooldown/fatigue reader asks (MAR-242).
 *
 * `reachedCustomer` AND not a reply turn (`isReplyTurnDecision`). The chat fast path logs a
 * `customer_message:chat` help for every message the shopper sends, `dispatched` then `executed`
 * (MAR-252). It reached them — but counting it meant a shopper who merely chatted reset their own
 * cooldown: `summariseHistory` read "last contact 0h ago" and the next cadence send (the then-live
 * inspiration sweep, `open_loop_due`, browse follow-up) was withheld for `minGapHours`, and 72h later an unclicked
 * reply read as the shopper IGNORING us.
 *
 * Keyed on the trigger, not on `judgment.source` — see `REPLY_TURN_TRIGGERS` for why, and for why
 * a requested `comparison` email still counts.
 *
 * The other reply paths (email reply, SMS reply, STOP/HELP, websocket-chat templates) write
 * `ANS#` reply records that never enter CustomerIndex, so they never reach this predicate at
 * all (`reply-caps-invariance.test.js`). Only the gateway's decision row needed the exclusion.
 *
 * Not the Ledger's question: the receipt (`decision-receipt.classifyDecision`) still files the
 * reply as sent, and the merchant still sees it.
 */
function countsAsContact(decision) {
  return reachedCustomer(decision) && !isReplyTurnDecision(decision);
}

/** True when this decision's outcome proves the customer engaged with the message. */
function isEngagedOutcome(outcome) {
  return ENGAGED_OUTCOMES.includes(outcome);
}

/** True when this decision's outcome means the customer bought. */
function isConversionOutcome(outcome) {
  return CONVERSION_OUTCOMES.includes(outcome);
}

/**
 * Did this decision fatigue its channel? (bug sweep #4 — replaces a predicate that matched
 * values nothing writes.)
 *
 * Two ways to fatigue, per the 2026-08-04 decision:
 *  1. An explicit dismissal on the negative axis — the strongest signal available.
 *  2. A help that was sent more than IGNORED_AFTER_MS ago and never advanced past
 *     sent/delivered — nobody opened it. Without this half, EMAIL fatigue would be
 *     near-dead: dismissals are on-site events, so an email channel would almost never
 *     reach its threshold.
 *
 * @param {object} decision - a decision row (or the compact `recentEngagements` projection)
 * @param {Date|number} [now]
 */
function isFatiguingOutcome(decision, now = Date.now()) {
  if (!decision) return false;
  if (decision.outcomeProjection?.delivery?.status === 'failed') return false;
  if (DISMISSAL_NEGATIVE_OUTCOMES.includes(decision.negativeOutcome)) return true;

  // A help judgment — canonical or a pre-cutover row's legacy `action: 'engage'` (MAR-65).
  if (!isHelpDecision(decision)) return false;
  // "Sent more than IGNORED_AFTER_MS ago and never opened" was the stated intent above, but
  // until now the code never checked the sending half — so a decision the enforcer itself
  // withheld aged into "the customer ignored me" and fatigued the channel it was never sent on.
  // `countsAsContact`, not bare `reachedCustomer`: a chat reply the shopper did not click is
  // not them ignoring outreach — there was no outreach (MAR-242).
  if (!countsAsContact(decision)) return false;
  if (isEngagedOutcome(decision.outcome)) return false;

  const sentAt = new Date(decision.createdAt).getTime();
  if (isNaN(sentAt)) return false;
  const t = now instanceof Date ? now.getTime() : now;
  return (t - sentAt) >= IGNORED_AFTER_MS;
}

module.exports = {
  POSITIVE_OUTCOMES,
  CONVERSION_OUTCOMES,
  ENGAGED_OUTCOMES,
  DISMISSAL_NEGATIVE_OUTCOMES,
  NOT_SENT_STATUSES,
  IGNORED_AFTER_MS,
  DELIVERY_REACHED,
  DELIVERY_NOT_REACHED,
  reachedCustomer,
  countsAsContact,
  isEngagedOutcome,
  isConversionOutcome,
  isFatiguingOutcome
};
